import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from "react";
import { BaseTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Base";
import { DesignLanguage, IColorMode } from "@swiggy-private/dls-web-common/dist/components/types";
import { ThemeProvider as DLSThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { CUSTOM_BASE_THEME, THEME_STORAGE_KEY, VALID_THEMES } from "./constants";
import { hasMinimumPlVersion } from "./helpers";
import { IThemeContext, IThemeProviderProps } from "./interface";
import { isApp } from "../ApiContext";
import { useDigitalMenuConfig } from "../DigitalMenuConfigContext/DigitalMenuConfigContext";
import { DigitalMenuVersion } from "../types";

// Utility functions
const isValidTheme = (theme: string | null): theme is IColorMode => {
    return theme !== null && VALID_THEMES.includes(theme as IColorMode);
};

const getStoredTheme = (): IColorMode | null => {
    if (typeof window === "undefined") return null;

    try {
        const storedTheme = sessionStorage.getItem(THEME_STORAGE_KEY);
        return isValidTheme(storedTheme) ? storedTheme : null;
    } catch {
        return null;
    }
};

const storeTheme = (theme: IColorMode): void => {
    if (typeof window === "undefined") return;

    try {
        sessionStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // Silently fail if storage is not available
    }
};

const ThemeContext = createContext<IThemeContext | undefined>(undefined);

export const ThemeProvider: React.FC<IThemeProviderProps> = ({
    children,
    defaultTheme = "dark",
    persistTheme = true,
    swiggyHeaders,
}) => {
    const [themeMode, setThemeModeState] = useState<IColorMode>(() => {
        // Initialize with stored theme or default
        return persistTheme ? (getStoredTheme() ?? defaultTheme) : defaultTheme;
    });

    const [baseTheme, setBaseThemeState] = useState<typeof BaseTheme>(BaseTheme);
    const [baseThemeVersion, setBaseThemeVersion] = useState(0);
    const { config } = useDigitalMenuConfig();

    // Track previous baseTheme to detect changes synchronously during render
    // Using ref instead of state to avoid race conditions - updates are synchronous
    const previousBaseThemeRef = useRef<typeof BaseTheme>(baseTheme);
    const isThemeUpdatingRef = useRef<boolean>(false);

    // Check if we're in V2 in-app environment (memoized to avoid recalculation)
    const isV2InApp = useMemo(
        () =>
            isApp() &&
            swiggyHeaders &&
            hasMinimumPlVersion(swiggyHeaders) &&
            config?.version === DigitalMenuVersion.V2,
        [config?.version, swiggyHeaders]
    );

    // Detect baseTheme changes SYNCHRONOUSLY during render to prevent race conditions
    // This must run during render (not in useEffect) so the flag is set BEFORE any
    // child component's useEffect can read it. State updates are async, refs are sync.
    const baseThemeChanged = baseTheme !== previousBaseThemeRef.current;
    if (baseThemeChanged) {
        if (isV2InApp) {
            isThemeUpdatingRef.current = true;
        }
        previousBaseThemeRef.current = baseTheme;
    }

    // Clear flag when theme stabilizes (same value in subsequent render after remount)
    useEffect(() => {
        if (isThemeUpdatingRef.current && baseTheme === previousBaseThemeRef.current) {
            isThemeUpdatingRef.current = false;
        }
    }, [baseTheme]);

    // Watch config changes and update theme accordingly
    useEffect(() => {
        if (!config || !isV2InApp) {
            return;
        }

        setThemeModeState("light");

        const themeValue = config.themeInfo?.theme;
        if (CUSTOM_BASE_THEME[themeValue]) {
            setBaseThemeState(CUSTOM_BASE_THEME[themeValue] as typeof BaseTheme);
        }
    }, [config, isV2InApp]);

    // Increment version counter when baseTheme changes to create a stable but changing key
    useEffect(() => {
        setBaseThemeVersion((prev) => prev + 1);
    }, [baseTheme]);

    // Save theme to sessionStorage when it changes
    useEffect(() => {
        if (persistTheme) {
            storeTheme(themeMode);
        }
    }, [themeMode, persistTheme]);

    const setThemeMode = useCallback((mode: IColorMode) => {
        setThemeModeState(mode);
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeModeState((prev) => (prev === "light" ? "dark" : "light"));
    }, []);

    const setCustomBaseTheme = useCallback((theme: typeof BaseTheme) => {
        setBaseThemeState(theme);
    }, []);

    // Construct the full theme object with colorMode and designLanguage
    // This is what DLSThemeProvider does internally, but it doesn't react to theme prop changes
    const fullTheme = useMemo(
        () => ({
            ...baseTheme,
            colorMode: themeMode,
            designLanguage: DesignLanguage.BASE,
        }),
        [baseTheme, themeMode]
    );

    // Only change key when baseTheme changes, not themeMode
    const themeKey = useMemo(() => {
        return `baseTheme-v${baseThemeVersion}`;
    }, [baseThemeVersion]);

    const contextValue: IThemeContext = {
        themeMode,
        setThemeMode,
        toggleTheme,
        baseTheme,
        setCustomBaseTheme,
        isThemeUpdating: isThemeUpdatingRef.current,
    };

    return (
        <ThemeContext.Provider value={contextValue}>
            <DLSThemeProvider key={themeKey} theme={fullTheme} colorMode={themeMode}>
                {children}
            </DLSThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useDigitalMenuTheme = (): IThemeContext => {
    const context = useContext(ThemeContext);

    if (context === undefined) {
        throw new Error("useDigitalMenuTheme must be used within a ThemeProvider");
    }

    return context;
};
